'use client';

import { Button, type ButtonProps } from '../ui/button';
import { cn } from '~/lib/utils';
import { ShoppingBasketIcon } from 'lucide-react';
import { useAddToBasket } from '~/hooks/product/use-add-to-basket';
import { usePathname } from 'next/navigation';

type AddToBasketButtonProps = {
  variant?: 'default' | 'small';
  productName: string;
  itemCode: string;
  quantity: number;
  className?: string;
  buttonVariant?: ButtonProps['variant'];
  showInBasket?: boolean;
  overrideText?: string;
  /**
   * consider wrapping in a useCallback
   * @returns void
   */
  onSuccess?: () => void;
};

export function AddToBasketButton({
  productName,
  itemCode,
  quantity,
  className,
  variant,
  showInBasket,
  buttonVariant = 'default',
  overrideText,
  onSuccess,
}: AddToBasketButtonProps) {
  const path = usePathname();
  const runOrderSimulate = path.startsWith('/basket');

  const { handleAddToBasket, isAddingToBasket, isInBasket, isInitalLoading } =
    useAddToBasket({
      itemCode,
      quantity,
      productName,
      showInBasket,
      onSuccess,
      runOrderSimulate,
    });

  const addToBasketText = overrideText ?? 'Add to Basket';
  if (variant === 'small') {
    return (
      <Button
        type="button"
        size="icon"
        variant={buttonVariant}
        className="add-to-basket-button aspect-square size-8"
        onClick={handleAddToBasket}
        loader={isAddingToBasket || isInitalLoading}
        disabled={isInitalLoading}
      >
        {isInBasket ? (
          <>
            {/* <CheckIcon className="size-5" /> */}
            <span className="sr-only">Added to Basket</span>
          </>
        ) : (
          <>
            <ShoppingBasketIcon className="size-5" />
            <span className="sr-only">{addToBasketText}</span>
          </>
        )}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      className={cn('add-to-basket-button', 'items-center gap-1', className)}
      onClick={handleAddToBasket}
      loader={isAddingToBasket || isInitalLoading}
      disabled={isInitalLoading || isAddingToBasket}
      data-testid="add-to-basket"
      variant={buttonVariant}
    >
      {isInBasket ? (
        <>
          {/* <CheckIcon className="size-4" /> */}
          Added to Basket
        </>
      ) : (
        addToBasketText
      )}
    </Button>
  );
}
